import { gql, QueryHookOptions, useQuery } from "@apollo/client";
import {
  QueryCalculationsInfo,
  QueryCalculationsInfoVariables
} from "./schema/QueryCalculationsInfo";
import {
  QueryDataDependencies,
  QueryDataDependenciesVariables
} from "./schema/QueryDataDependencies";
import {
  QueryLatestVersion,
  QueryLatestVersionVariables
} from "./schema/QueryLatestVersion";
import {
  QueryTopicsEssentials,
  QueryTopicsEssentialsVariables,
  QueryTopicsEssentials_solutions_Solutions_solution_Solution_useCases_UseCasesViewModel_useCase_UseCaseViewModel_detail_UseCaseDetail as UseCaseDetail
} from "./schema/QueryTopicsEssentials";
import {
  QueryUploadDataInfo,
  QueryUploadDataInfoVariables
} from "./schema/QueryUploadDataInfo";

export type { UseCaseDetail };

const queryTopicsEssentials = gql`
  query QueryTopicsEssentials(
    $solutionKey: Key!
    $useCaseKey: Key!
    $useCaseVersion: String
  ) {
    solutions {
      solution(solutionKey: $solutionKey) {
        key
        useCases {
          useCase(useCaseKey: $useCaseKey, version: $useCaseVersion) {
            detail {
              version
              tags
              name
              description
              image
              useCaseKey: key
              transactionTable
            }
          }
        }
      }
    }
  }
`;

/* The information used in this query should be limited to what is essentially
 *  needed to display a use case topic.  */
export const useQueryTopicsEssentials = (
  variables: QueryTopicsEssentialsVariables
) =>
  useQuery<QueryTopicsEssentials, QueryTopicsEssentialsVariables>(
    queryTopicsEssentials,
    { variables }
  );

const queryUploadDataInfo = gql`
  query QueryUploadDataInfo(
    $solutionKey: Key!
    $useCaseKey: Key!
    $useCaseVersion: String
  ) {
    solutions {
      solution(solutionKey: $solutionKey) {
        key
        useCases {
          useCase(useCaseKey: $useCaseKey, version: $useCaseVersion) {
            data {
              address
            }
          }
        }
      }
    }
  }
`;

export const useQueryUploadDataInfo = (
  variables: QueryUploadDataInfoVariables
) => {
  return useQuery<QueryUploadDataInfo, QueryUploadDataInfoVariables>(
    queryUploadDataInfo,
    { variables }
  );
};

const queryCalculationsInfo = gql`
  query QueryCalculationsInfo(
    $solutionKey: Key!
    $useCaseKey: Key!
    $useCaseVersion: String
  ) {
    solutions {
      solution(solutionKey: $solutionKey) {
        key
        useCases {
          useCase(useCaseKey: $useCaseKey, version: $useCaseVersion) {
            myCalculations: calculations(scheduler: "me") {
              id
            }
            allCalculations: calculations {
              id
            }
          }
        }
      }
    }
  }
`;

export const useQueryCalculationsInfo = (
  variables: QueryCalculationsInfoVariables
) =>
  useQuery<QueryCalculationsInfo, QueryCalculationsInfoVariables>(
    queryCalculationsInfo,
    { variables }
  );

export const queryDataDependencies = gql`
  query QueryDataDependencies(
    $solutionKey: Key!
    $useCaseKey: Key!
    $useCaseVersion: String
  ) {
    solutions {
      solution(solutionKey: $solutionKey) {
        key
        useCases {
          useCase(useCaseKey: $useCaseKey, version: $useCaseVersion) {
            dataDependencies {
              icon
              name
            }
          }
        }
      }
    }
  }
`;

export const useQueryUseCaseDataDependencies = (
  variables: QueryDataDependenciesVariables,
  options?: QueryHookOptions<
    QueryDataDependencies,
    QueryDataDependenciesVariables
  >
) =>
  useQuery<QueryDataDependencies, QueryDataDependenciesVariables>(
    queryDataDependencies,
    { variables, ...options }
  );

const queryLatestVersion = gql`
  query QueryLatestVersion($solutionKey: Key!, $useCaseKey: Key!) {
    solutions {
      solution(solutionKey: $solutionKey) {
        key
        useCases {
          latestUseCase: useCase(useCaseKey: $useCaseKey) {
            detail {
              version
            }
          }
        }
      }
    }
  }
`;

export const useQueryLatestVersion = (variables: QueryLatestVersionVariables) =>
  useQuery<QueryLatestVersion, QueryLatestVersionVariables>(
    queryLatestVersion,
    { variables }
  );
